import { useState, useEffect } from "react";
import { Menu, X, ArrowRight, Play, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import logo from "./favicon.png";
import { ImageWithFallback } from "./figma/ImageWithFallback";

export function Hero() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMenuOpen(false);
    }
  };

  const stats = [
    { value: "100%", label: "Traceable", description: "Origin Verified" },
    { value: "ISCC", label: "Certified", description: "Export Ready" },
    { value: "IoT", label: "Enabled", description: "Real-Time" },
  ];

  return (
    <div className="relative bg-white overflow-hidden">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-xl shadow-lg"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 lg:h-24">
            {/* Logo and Company Name */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-3"
            >
              <img src={logo} alt="KRB Clean Energy" className="h-12 lg:h-16 w-auto" />
              <div>
                <div className="text-sm sm:text-base lg:text-xl font-black text-gray-900 tracking-tight leading-tight">
                  KRB Clean Energy Pvt Ltd
                </div>
                <div className="text-[10px] sm:text-xs lg:text-sm font-semibold text-[#7CB342] pt-0.5">
                  Where Waste Becomes Power
                </div>
              </div>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {["About", "Products", "Technology", "Process", "Contact"].map((item, index) => (
                <motion.button
                  key={item}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="text-base font-semibold text-gray-700 hover:text-[#7CB342] transition-colors relative group"
                >
                  {item}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#7CB342] to-[#1976D2] group-hover:w-full transition-all duration-300" />
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                onClick={() => scrollToSection("contact")}
                className="bg-gradient-to-r from-[#7CB342] to-[#1976D2] text-white px-8 py-3 rounded-full font-bold hover:shadow-xl hover:shadow-[#7CB342]/30 transition-all transform hover:scale-105"
              >
                Get Started
              </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-gray-900"
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:hidden bg-white border-t border-gray-200"
          >
            <div className="px-6 py-6 space-y-4">
              {["About", "Products", "Technology", "Process", "Contact"].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="block w-full text-left text-lg font-semibold text-gray-700 hover:text-[#7CB342] py-2"
                >
                  {item}
                </button>
              ))}
              <button
                onClick={() => scrollToSection("contact")}
                className="w-full bg-gradient-to-r from-[#7CB342] to-[#1976D2] text-white px-8 py-3 rounded-full font-bold"
              >
                Get Started
              </button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <div className="relative pt-32 lg:pt-40 pb-20 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7CB342]/10 to-[#1976D2]/10 border-2 border-[#7CB342]/30 rounded-full px-5 py-2.5"
              >
                <div className="w-2 h-2 bg-[#7CB342] rounded-full animate-pulse" />
                <span className="text-sm font-bold text-gray-900">
                  India's Leading UCO Aggregator
                </span>
              </motion.div>

              {/* Main Heading */}
              <div>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="text-5xl lg:text-6xl xl:text-7xl font-black text-gray-900 leading-[1.1] mb-6"
                >
                  Transforming
                  <span className="block bg-gradient-to-r from-[#7CB342] to-[#1976D2] bg-clip-text text-transparent mt-2">
                    Waste Into Power
                  </span>
                </motion.h1>
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="flex items-center gap-3 mb-6"
                >
                  <div className="h-1 w-12 bg-gradient-to-r from-[#7CB342] to-[#1976D2]" />
                  <p className="text-xl lg:text-2xl font-bold text-gray-700">
                    Where Waste Becomes Power
                  </p>
                </motion.div>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="text-lg lg:text-xl text-gray-600 leading-relaxed max-w-2xl"
                >
                  Converting <span className="font-bold text-gray-900">Used Cooking Oil</span> into{" "}
                  <span className="font-bold text-[#7CB342]">ISCC-certified</span> renewable fuel feedstock 
                  through <span className="font-bold text-[#1976D2]">IoT-enabled</span> digital traceability.
                </motion.p>
              </div>

              {/* CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <button
                  onClick={() => scrollToSection("products")}
                  className="group bg-gradient-to-r from-[#7CB342] to-[#1976D2] text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-[#7CB342]/30 transition-all transform hover:scale-105 flex items-center justify-center gap-2"
                >
                  Explore Solutions
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => scrollToSection("contact")}
                  className="group bg-white border-2 border-gray-900 text-gray-900 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-900 hover:text-white transition-all transform hover:scale-105 flex items-center justify-center gap-2"
                >
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="grid grid-cols-3 gap-4 pt-8 border-t border-gray-200"
              >
                {stats.map((stat, index) => (
                  <div key={index} className="text-center lg:text-left">
                    <div className="text-3xl lg:text-4xl font-black bg-gradient-to-r from-[#7CB342] to-[#1976D2] bg-clip-text text-transparent mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm font-bold text-gray-900">{stat.label}</div>
                    <div className="text-xs text-gray-600">{stat.description}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="relative"
            >
              {/* Main Image Card */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1729909019771-7728f56647f4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx1c2VkJTIwY29va2luZyUyMG9pbCUyMHJlc3RhdXJhbnR8ZW58MXx8fHwxNzY1MDg5NzQxfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt="Used Cooking Oil Collection"
                  className="w-full h-[500px] lg:h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent" />
                
                {/* Floating Badge */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute bottom-8 left-8 right-8 bg-white rounded-2xl p-6 shadow-2xl"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold text-gray-600 mb-1">Total Impact</div>
                      <div className="text-3xl font-black bg-gradient-to-r from-[#7CB342] to-[#1976D2] bg-clip-text text-transparent">
                        2G & 3G Feedstock
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-[#7CB342] to-[#1976D2] rounded-full p-4">
                      <CheckCircle2 className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#7CB342]/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-[#1976D2]/20 rounded-full blur-3xl" />
            </motion.div>
          </div>
        </div>

        {/* Background Pattern */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-[#7CB342]/5 to-transparent" />
          <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-[#1976D2]/5 to-transparent" />
          <svg className="absolute inset-0 w-full h-full opacity-[0.02]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
      </div>
    </div>
  );
}